import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { fetchMasteryFromDB, MasteryRecord } from '../../../hooks/useQuizMasteryDB';
import { getMasteryRecord } from '../../../hooks/useQuizMastery';
import AuthModal from '../../../components/feature/AuthModal';

interface QuizQuestion {
  id: string;
  subject: string;
  subcategory: string | null;
}

interface SubjectStats {
  subject: string;
  total: number;
  once: number;
  mastered: number;
}

interface QuizMasteryOverviewProps {
  layoutMode?: 'normal' | 'compact';
}

export default function QuizMasteryOverview({ layoutMode = 'normal' }: QuizMasteryOverviewProps) {
  const { user, loading: authLoading } = useAuth();
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [mastery, setMastery] = useState<MasteryRecord>({});
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (user) {
      fetchMasteryFromDB().then((record) => setMastery(record));
    } else {
      setMastery(getMasteryRecord());
    }
  }, [user]);

  const fetchQuestions = async () => {
    try {
      const { data, error } = await supabase
        .from('quiz_questions')
        .select('id, subject, subcategory');
      if (error) throw error;
      setQuestions((data as QuizQuestion[]) ?? []);
    } catch (e) {
      console.error('Failed to fetch quiz questions for mastery:', e);
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const overallTotal = questions.length;
  const overallOnce = useMemo(
    () => questions.filter((q) => {
      const level = mastery[q.id]?.correct_count || 0;
      return level === 1;
    }).length,
    [questions, mastery]
  );
  const overallMastered = useMemo(
    () => questions.filter((q) => {
      const level = mastery[q.id]?.correct_count || 0;
      return level >= 2;
    }).length,
    [questions, mastery]
  );
  const overallUntouched = overallTotal - overallOnce - overallMastered;
  const overallPct =
    overallTotal > 0 ? Math.round(((overallMastered + overallOnce) / overallTotal) * 100) : 0;
  const masteredPct = overallTotal > 0 ? Math.round((overallMastered / overallTotal) * 100) : 0;

  const stats = useMemo(() => {
    const subjectMap: Record<
      string,
      { total: number; once: number; mastered: number }
    > = {};
    questions.forEach((q) => {
      if (!subjectMap[q.subject]) {
        subjectMap[q.subject] = { total: 0, once: 0, mastered: 0 };
      }
      const s = subjectMap[q.subject];
      s.total++;
      const level = mastery[q.id]?.correct_count || 0;
      if (level === 1) s.once++;
      else if (level >= 2) s.mastered++;
    });

    const result: SubjectStats[] = Object.entries(subjectMap).map(
      ([subject, data]) => ({
        subject,
        ...data,
      })
    );
    return result.sort((a, b) => b.total - a.total);
  }, [questions, mastery]);

  if (loading || authLoading) {
    return (
      <div className="bg-gray-100 rounded-xl border border-gray-300 p-6 animate-pulse h-48"></div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="bg-gray-100 rounded-xl border border-gray-300 p-6 text-center">
        <div className="w-12 h-12 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-3">
          <i className="ri-question-line text-xl text-gray-400"></i>
        </div>
        <p className="text-sm text-gray-500 mb-3">クイズがまだ登録されていません</p>
        <Link
          to="/quiz"
          className="inline-flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
        >
          クイズページへ
          <i className="ri-arrow-right-line text-sm"></i>
        </Link>
      </div>
    );
  }

  // Not logged in - show login prompt
  if (!user) {
    return (
      <>
        <div className="bg-gray-100 rounded-xl border border-gray-300 p-5 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full mx-auto mb-3">
            <i className="ri-bar-chart-box-line text-xl text-white"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mb-1">クイズ習熟度</h3>
          <p className="text-xs text-gray-500 mb-4">
            習熟度の表示にはログインが必要です
          </p>
          <button
            onClick={() => setShowAuth(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-xs font-medium hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-login-box-line text-sm"></i>
            ログイン
          </button>
        </div>
        {showAuth && (
          <AuthModal onClose={() => setShowAuth(false)} onSuccess={() => setShowAuth(false)} />
        )}
      </>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${layoutMode === 'compact' ? 'p-4' : 'p-6'}`}>
      <h3 className={`font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2 ${layoutMode === 'compact' ? 'text-base' : 'text-lg'}`}>
        <i className="ri-trophy-line text-yellow-500"></i>
        クイズマスタリー
      </h3>

      <div className={`space-y-3 ${layoutMode === 'compact' ? 'text-sm' : ''}`}>
        {stats.map((item) => {
          const pct = item.total > 0 ? Math.round(((item.mastered + item.once) / item.total) * 100) : 0;
          return (
            <div key={item.subject}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-gray-700 dark:text-gray-300 truncate max-w-[60%]">{item.subject}</span>
                <span className="font-bold text-teal-600 dark:text-teal-400">{pct}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-teal-400 to-emerald-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className={`grid grid-cols-3 gap-3 ${layoutMode === 'compact' ? 'mt-4' : 'mt-6'}`}>
        <div className={`text-center ${layoutMode === 'compact' ? 'p-2' : 'p-3'} bg-green-50 dark:bg-green-900/20 rounded-lg`}>
          <p className={`font-bold text-green-600 dark:text-green-400 ${layoutMode === 'compact' ? 'text-lg' : 'text-2xl'}`}>
            {overallTotal}
          </p>
          <p className={`text-gray-600 dark:text-gray-400 ${layoutMode === 'compact' ? 'text-xs' : 'text-sm'}`}>総問題数</p>
        </div>
        <div className={`text-center ${layoutMode === 'compact' ? 'p-2' : 'p-3'} bg-blue-50 dark:bg-blue-900/20 rounded-lg`}>
          <p className={`font-bold text-blue-600 dark:text-blue-400 ${layoutMode === 'compact' ? 'text-lg' : 'text-2xl'}`}>
            {overallMastered}
          </p>
          <p className={`text-gray-600 dark:text-gray-400 ${layoutMode === 'compact' ? 'text-xs' : 'text-sm'}`}>習得済み</p>
        </div>
        <div className={`text-center ${layoutMode === 'compact' ? 'p-2' : 'p-3'} bg-purple-50 dark:bg-purple-900/20 rounded-lg`}>
          <p className={`font-bold text-purple-600 dark:text-purple-400 ${layoutMode === 'compact' ? 'text-lg' : 'text-2xl'}`}>
            {masteredPct}%
          </p>
          <p className={`text-gray-600 dark:text-gray-400 ${layoutMode === 'compact' ? 'text-xs' : 'text-sm'}`}>平均習得率</p>
        </div>
      </div>
    </div>
  );
}
